import { Box } from '@utilitywarehouse/hearth-react';
import type { BoxProps } from '@utilitywarehouse/hearth-react';
import type { CSSProperties, ElementType, ReactNode } from 'react';

interface SectionContainerProps {
  children: ReactNode;
  /** Renders a semantic HTML element (e.g. 'header', 'footer') instead of a div */
  as?: ElementType;
  backgroundColor?: string;
  paddingY?: BoxProps['paddingY'];
  paddingX?: BoxProps['paddingX'];
  style?: CSSProperties;
  dataTestId?: string;
}

const DEFAULT_PADDING_TOP = {
  mobile: '300',
  tablet: '300',
  desktop: '400',
} as const;
const DEFAULT_PADDING_BOTTOM = {
  mobile: '400',
  tablet: '400',
  desktop: '400',
} as const;
const DEFAULT_PADDING_X = {
  mobile: '200',
  tablet: '400',
  desktop: '400',
} as const;

/**
 * SectionContainer - A consistent container for page sections
 *
 * Default padding follows Hearth container tokens:
 * - Mobile: 24px top, 32px bottom, 16px sides
 * - Tablet: 24px top, 32px bottom, 32px sides
 * - Desktop: 32px top/bottom, 32px sides
 *
 * Can be overridden for special sections like Hero
 */
function SectionContainer({
  children,
  as,
  backgroundColor,
  paddingY,
  paddingX,
  style,
  dataTestId,
}: SectionContainerProps) {
  const resolvedPaddingTop = paddingY || DEFAULT_PADDING_TOP;
  const resolvedPaddingBottom = paddingY || DEFAULT_PADDING_BOTTOM;
  const resolvedPaddingX = paddingX || DEFAULT_PADDING_X;

  const Element = as || 'div';

  return (
    <Box
      asChild
      width="100%"
      style={{
        backgroundColor,
        ...style,
      }}
      paddingTop={resolvedPaddingTop}
      paddingBottom={resolvedPaddingBottom}
      paddingX={resolvedPaddingX}
      data-testid={dataTestId}
    >
      <Element>{children}</Element>
    </Box>
  );
}

export default SectionContainer;
